/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import type { AxiosInstance } from 'axios';
import { hasRefreshToken } from './auth-interceptor-shared';
import { triggerRefresh } from './client-refresh';
import { getAuthToken } from './get-token';
import { terminalLogout } from './terminal-logout';
import { webviewHttpClient as mHttpClient } from '@/app/[locale]/(m)/components/auth/webview-http-client';
import { webviewHttpClient as orderHttpClient } from '@/app/[locale]/(order)/components/auth/webview-http-client';

/**
 * 401 handling cho các API đi qua `webviewHttpClient` (đặt đơn, ước tính phí, lưu
 * session, địa chỉ, rating...).
 *
 * Vì sao cần file này: các API này KHÔNG đi qua `publicHttpClient` nên KHÔNG được
 * 2-tier refresh wrapper của web interceptor (install-axios-interceptor.ts) bảo vệ.
 * Hệ quả (bug đang sửa): với WEB login, khi token hết hạn → order/estimate trả 401
 * nhưng không có interceptor nào bắt → mutation reject → onError hiện toast sai
 * ("có thay đổi về giá...") → user KẸT: không đặt được đơn, không bị đăng xuất, không
 * biết phiên đã hết hạn.
 *
 * Hành vi (CHỈ web — có cookie refresh_token):
 *  - 401 + chưa retry → triggerRefresh → retry request 1 lần với token mới.
 *  - Refresh fail (refresh_token cũng invalid) → terminalLogout (clear + toast + về /).
 *  - Webinapp (KHÔNG có refresh_token) → KHÔNG đụng tới (reject như cũ → giữ phiên,
 *    đúng thiết kế webinapp; user mở lại từ app để lấy token mới).
 *
 * Có 2 instance webviewHttpClient riêng biệt ((m) và (order)) — cài trên cả hai.
 * Idempotent với web interceptor: chỉ act khi hasRefreshToken() = true; còn luồng FB
 * webview (WebviewSessionProvider) dùng session access_token (không có refresh_token)
 * nên nhánh này tự bỏ qua, không xung đột.
 */

let installed = false;

export function installWebviewAuthInterceptor() {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  attach401Logout(mHttpClient);
  attach401Logout(orderHttpClient);
}

function attach401Logout(client: AxiosInstance) {
  client.interceptors.response.use(
    (res) => res,
    async (error) => {
      const status = error?.response?.status;
      const original = error?.config;
      // Chỉ xử lý 401 chưa retry của WEB session. Webinapp giữ phiên như cũ.
      if (
        status !== 401 ||
        !original ||
        original._retry ||
        !hasRefreshToken()
      ) {
        return Promise.reject(error);
      }
      original._retry = true;

      const ok = await triggerRefresh();
      if (!ok) {
        // refresh_token cũng invalid → terminal logout (báo user + về trang chủ).
        terminalLogout();
        return Promise.reject(error);
      }

      const newToken = getAuthToken();
      if (newToken && original.headers) {
        original.headers.Authorization = `Bearer ${newToken}`;
      }
      return client.request(original);
    }
  );
}
